import type { AxiosRequestConfig } from 'axios';
import 'dotenv/config';
import { HttpProxyAgent } from 'http-proxy-agent';
import { HttpsProxyAgent } from 'https-proxy-agent';
import { getProxyForUrl } from 'proxy-from-env';

type ProxyDecision = {
  proxyUrl: string | null;
  noProxy: string;
};

function isIpv4(value: string) {
  const parts = value.split('.');
  if (parts.length !== 4) return false;
  return parts.every((part) => {
    const num = Number(part);
    return Number.isInteger(num) && num >= 0 && num <= 255;
  });
}

function ipv4ToInt(value: string) {
  return value
    .split('.')
    .map((part) => Number(part))
    .reduce((acc, part) => (acc << 8) + part, 0);
}

function isCidrMatch(hostname: string, cidr: string) {
  const [range, bitsString] = cidr.split('/');
  if (!range || !bitsString) return false;
  if (!isIpv4(hostname) || !isIpv4(range)) return false;

  const bits = Number(bitsString);
  if (!Number.isInteger(bits) || bits < 0 || bits > 32) return false;

  const mask = bits === 0 ? 0 : (~0 << (32 - bits)) >>> 0;
  return (ipv4ToInt(hostname) & mask) === (ipv4ToInt(range) & mask);
}

function splitHostPort(entry: string) {
  const lastColon = entry.lastIndexOf(':');
  if (lastColon > -1 && entry.indexOf(':') === lastColon) {
    const host = entry.slice(0, lastColon);
    const port = entry.slice(lastColon + 1);
    if (host && port) {
      return { host, port };
    }
  }
  return { host: entry, port: '' };
}

function isNoProxyMatch(url: string, noProxy: string) {
  if (!noProxy) return false;
  let hostname = '';
  let port = '';
  try {
    const parsed = new URL(url);
    hostname = parsed.hostname.toLowerCase();
    port = parsed.port;
  } catch {
    return false;
  }

  const entries = noProxy
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);

  for (const entry of entries) {
    if (entry === '*') return true;

    if (entry.includes('/')) {
      if (isCidrMatch(hostname, entry)) return true;
      continue;
    }

    const { host, port: entryPort } = splitHostPort(entry.toLowerCase());
    if (entryPort && port && entryPort !== port) {
      continue;
    }

    if (host.startsWith('.')) {
      if (hostname.endsWith(host)) return true;
      continue;
    }

    if (hostname === host) return true;
    if (hostname.endsWith(`.${host}`)) return true;
  }

  return false;
}

function getProxyDecision(url: string): ProxyDecision {
  const noProxy = process.env['NO_PROXY'] || process.env['no_proxy'] || '';

  if (!url) {
    return { proxyUrl: null, noProxy };
  }

  if (isNoProxyMatch(url, noProxy)) {
    return { proxyUrl: null, noProxy };
  }

  try {
    const proxyUrl = getProxyForUrl(url);
    return { proxyUrl: proxyUrl || null, noProxy };
  } catch {
    return { proxyUrl: null, noProxy };
  }
}

export function buildAxiosConfig(url: string, headers: Record<string, string>): AxiosRequestConfig {
  const config: AxiosRequestConfig = {
    headers,
    proxy: false,
  };

  const { proxyUrl } = getProxyDecision(url);
  if (!proxyUrl) {
    return config;
  }

  if (url.startsWith('https:')) {
    config.httpsAgent = new HttpsProxyAgent(proxyUrl);
  } else {
    config.httpAgent = new HttpProxyAgent(proxyUrl);
  }

  return config;
}
